import { motion } from "framer-motion";
import { MapPin, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BackButton from "../components/BackButton";
import { useAuth } from "../context/AuthContext";
import api, { states } from "../services/api";

const RaiseIssue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading: authLoading, user } = useAuth();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [loading, setLoading] = useState(false);
  const [usePermanentAddress, setUsePermanentAddress] = useState(true);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [videoPreview, setVideoPreview] = useState("");
  const [assignedRepresentative, setAssignedRepresentative] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Road",
    customCategory: "",
    state: searchParams.get("state") || "",
    district: "",
    constituency: searchParams.get("constituency") || "",
    village: "",
    municipality: "",
    assignedToType: (searchParams.get("to") || "").toUpperCase(),
    assignedToName: searchParams.get("name") || "",
    isAnonymous: false,
    geotag: "",
  });

  useEffect(() => {
    if (authLoading || !user) {
      return;
    }

    if (user?.permanentAddress && usePermanentAddress) {
      setFormData((current) => ({
        ...current,
        state: user.permanentAddress.state || current.state,
        district: user.permanentAddress.district || current.district,
        constituency:
          user.permanentAddress.mlaConstituency ||
          user.permanentAddress.mpConstituency ||
          current.constituency,
      }));
    }
  }, [authLoading, user, usePermanentAddress]);

  useEffect(() => {
    const loadRepresentative = async () => {
      if (!formData.state || !formData.constituency) {
        setAssignedRepresentative(null);
        return;
      }

      try {
        const response = await states.lookupRepresentative({
          state: formData.state,
          constituency: formData.constituency,
          position: formData.assignedToType || undefined,
        });
        setAssignedRepresentative(response.data);
        setFormData((current) => ({
          ...current,
          assignedToType: response.data.position || current.assignedToType,
          assignedToName: response.data.name || current.assignedToName,
        }));
      } catch (error) {
        setAssignedRepresentative(null);
      }
    };

    loadRepresentative();
  }, [formData.state, formData.constituency, formData.assignedToType]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (event.target.name === "images") {
      const newImages = files.filter((file) => file.type.startsWith("image/"));
      setImages((current) => [...current, ...newImages]);
      setPreviewUrls((current) => [
        ...current,
        ...newImages.map((file) => URL.createObjectURL(file)),
      ]);
      return;
    }

    const videoFile = files[0];
    if (videoFile) {
      setVideo(videoFile);
      setVideoPreview(URL.createObjectURL(videoFile));
    }
  };

  const removeImage = (index) => {
    setImages((current) => current.filter((_, imageIndex) => imageIndex !== index));
    setPreviewUrls((current) => current.filter((_, imageIndex) => imageIndex !== index));
  };

  const removeVideo = () => {
    setVideo(null);
    setVideoPreview("");
  };

  const handleGeotag = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = `${position.coords.latitude},${position.coords.longitude}`;
        setFormData((current) => ({ ...current, geotag: coords }));
        toast.success("Location added");
      },
      () => toast.error("Failed to fetch location."),
    );
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => payload.append(key, value));
      images.forEach((image) => payload.append("files", image));
      if (video) {
        payload.append("video", video);
      }

      await api.post("/issues/raise", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Issue raised successfully!");
      navigate("/voter-dashboard");
    } catch (error) {
      console.error("Issue raise error:", error);
      toast.error(error?.response?.data?.message || "Error raising issue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <BackButton className="mb-4" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-xl shadow-lg"
        >
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Raise a New Issue</h1>
          <form onSubmit={handleSubmit} className="space-y-6">
            <input
              type="text"
              name="title"
              placeholder="Issue Title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />

            <textarea
              rows="4"
              name="description"
              placeholder="Describe the issue..."
              value={formData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />

            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full p-3 border rounded-lg"
            >
              {["Road", "Water", "Electricity", "Women", "Unemployment", "School", "Police", "Health", "Admin", "Other"].map(
                (category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ),
              )}
            </select>

            {formData.category === "Other" && (
              <input
                type="text"
                name="customCategory"
                placeholder="Custom Category"
                value={formData.customCategory}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg"
              />
            )}

            <div className="flex gap-2 items-center">
              <input
                id="usePermanentAddress"
                type="checkbox"
                checked={usePermanentAddress}
                onChange={() => setUsePermanentAddress((current) => !current)}
              />
              <label htmlFor="usePermanentAddress" className="text-gray-700">
                Use Permanent Address
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="state"
                placeholder="State"
                value={formData.state}
                onChange={handleChange}
                required
                className="w-full p-3 border rounded-lg"
              />
              <input
                type="text"
                name="district"
                placeholder="District"
                value={formData.district}
                onChange={handleChange}
                required
                className="w-full p-3 border rounded-lg"
              />
            </div>

            <input
              type="text"
              name="constituency"
              placeholder="Constituency"
              value={formData.constituency}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
            />

            {assignedRepresentative && (
              <div className="rounded-xl border bg-green-50 p-4 text-sm text-green-800">
                Auto-assigned to {assignedRepresentative.position}: {assignedRepresentative.name}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="village"
                placeholder="Village"
                value={formData.village}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg"
              />
              <input
                type="text"
                name="municipality"
                placeholder="Municipality"
                value={formData.municipality}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg"
              />
            </div>

            <button
              type="button"
              onClick={handleGeotag}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <MapPin className="w-5 h-5" /> Add Current Location
            </button>

            <label className="border-2 border-dashed rounded-lg p-4 text-center text-gray-500 cursor-pointer block">
              <input
                type="file"
                name="images"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <Upload className="w-6 h-6 mx-auto mb-2" />
              Upload Images
            </label>

            {previewUrls.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {previewUrls.map((url, index) => (
                  <div key={url} className="relative">
                    <img src={url} className="w-full h-20 object-cover rounded" alt="Preview" />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="border-2 border-dashed rounded-lg p-4 text-center text-gray-500 cursor-pointer block">
              <input
                type="file"
                name="video"
                accept="video/*"
                className="hidden"
                onChange={handleFileChange}
              />
              <Upload className="w-6 h-6 mx-auto mb-2" />
              Upload Video
            </label>

            {videoPreview && (
              <div className="relative">
                <video src={videoPreview} controls className="w-full h-32 rounded" />
                <button
                  type="button"
                  onClick={removeVideo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                id="isAnonymous"
                name="isAnonymous"
                type="checkbox"
                checked={formData.isAnonymous}
                onChange={handleChange}
              />
              <label htmlFor="isAnonymous" className="text-gray-700">
                Raise as Anonymous
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
            >
              {loading ? "Submitting..." : "Raise Issue"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RaiseIssue;
